import clsx from 'clsx'
import React, { useRef, useState } from 'react'
import { Link, LinkProps } from 'react-router-dom'

import { IoIosArrowDown } from 'react-icons/io'
import { RiLinkM } from 'react-icons/ri'
import { useCopyToClipboard, useOnClickOutside } from 'usehooks-ts'
import { INPUT_SIZES, INPUT_VARIANTS } from '../../utils/constants'
import { SystemTooltip } from '../ui/Tooltip'

type CustomDivProps = ({
    as?: 'div'
    disabled?: boolean
} & React.HTMLAttributes<HTMLDivElement>)

type CustomButtonProps = ({
    as?: 'button'
} & React.ButtonHTMLAttributes<HTMLButtonElement>)

type CustomLinkProps = ({
    as?: 'link'
    isExternal?: boolean
    disabled?: boolean
} & LinkProps)

type ButtonProps = {
    inputVariant?: keyof typeof INPUT_VARIANTS;
    inputSize?: keyof typeof INPUT_SIZES;
    isLoading?: boolean;
    tooltip?: string;
    indicator?: string;

    shape?: 'rectangle' | 'square';
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;

    hidden?: boolean
    showHover?: boolean;
    showCorners?: boolean;
    showBorders?: boolean;
} & (CustomButtonProps | CustomLinkProps | CustomDivProps)

export const Button: React.FC<ButtonProps> = (props) => {
    const {
        as = 'button',
        inputVariant = 'deep_ocean_text_white_bg',
        inputSize = 'fit',
        isLoading = false,
        disabled = false,
        tooltip,
        indicator,
        children,
        className,
        rightIcon,
        leftIcon,
        shape = 'rectangle',
        hidden = false,
        showHover = true,
        showCorners = true,
        showBorders = true,
        ...restOfProps
    } = props

    // const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    //     if (disabled) {
    //         return
    //     }
    // }

    const commonClassName = clsx(
        "h-10 relative flex justify-center items-center gap-2 border-[1px]",
        "transition-all duration-300 ease-in-out",
        'font-semibold',
        (showCorners) && "rounded-md",
        (showHover && !disabled) && "hover:opacity-90",
        (!showBorders) && "!border-0",
        disabled ? "cursor-not-allowed opacity-25" : "cursor-pointer",
        INPUT_VARIANTS[inputVariant],
        INPUT_VARIANTS['common_text'],
        INPUT_SIZES[inputSize],
        className
    )

    const commonElement = (
        <div
            className={clsx(
                'relative flex items-center',
                shape === 'rectangle' && 'px-4',
            )}
        >
            {leftIcon}

            <div
                className={clsx(
                    "w-fit truncate",
                    shape === 'rectangle' ? 'px-2 py-1' : 'p-1',
                )}
            >
                {isLoading ? `. . .` : children}
            </div>

            {rightIcon}

            {indicator && (
                <div className='absolute -top-2 -right-2 w-5 h-5 rounded-full bg-cs-500 text-white font-bold flex justify-center items-center px-1.5'>
                    {indicator}
                </div>
            )}
        </div>
    )

    if (hidden) {
        return null
    }

    if (disabled || isLoading) {
        const { ...restOfDivProps } = restOfProps as CustomDivProps

        return (
            <SystemTooltip
                message={tooltip}
            >
                <div
                    className={commonClassName}
                    {...restOfDivProps}
                >
                    {commonElement}
                </div>
            </SystemTooltip>
        )
    }

    if (as === 'link') {
        const { to, isExternal, ...restOfLinkProps } = restOfProps as CustomLinkProps

        if (isExternal) {
            return (
                <SystemTooltip
                    message={tooltip}
                >
                    <a
                        {...restOfLinkProps}
                        className={commonClassName}
                        href={to as string}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {commonElement}
                    </a>
                </SystemTooltip>
            )
        }

        return (
            <SystemTooltip
                message={tooltip}
            >
                <Link
                    {...restOfLinkProps}
                    className={commonClassName}
                    to={to}
                >
                    {commonElement}
                </Link>
            </SystemTooltip>
        )
    }

    if (as === 'div') {
        const { ...restOfDivProps } = restOfProps as CustomDivProps

        return (
            <SystemTooltip
                message={tooltip}
            >
                <div
                    {...restOfDivProps}
                    className={commonClassName}
                >
                    {commonElement}
                </div>
            </SystemTooltip>
        )
    }

    const { ...restOfButtonProps } = restOfProps as CustomButtonProps

    return (
        <SystemTooltip
            message={tooltip}
        >
            <button
                type='button'
                {...restOfButtonProps}
                disabled={disabled || isLoading}
                className={commonClassName}
            >
                {commonElement}
            </button>
        </SystemTooltip>
    )
}

interface ButtonGroupProps {
    labelComponent: React.ReactNode;

    triggerOptions?: {
        variant?: keyof typeof INPUT_VARIANTS;
    };
    menuOptions?: {
        position: 'left' | 'right'
    };
    buttons: Array<{
        shouldCloseOnClick: boolean;
        component: React.ReactNode
    }>
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({ labelComponent, triggerOptions, menuOptions, buttons }) => {
    const menuRef = useRef<HTMLDivElement | null>(null)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const handleCloseDropdown = () => {
        setIsDropdownOpen(false);
    };

    const handleClickArrow = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        setIsDropdownOpen((prevValue) => !prevValue)
    }

    const handleClickButton = (shouldCloseOnClick: boolean) => {
        if (shouldCloseOnClick) {
            handleCloseDropdown()
        }
    }

    useOnClickOutside(menuRef as React.RefObject<HTMLDivElement>, handleCloseDropdown);

    return (
        <div className='relative inline-flex'>
            <div
                className={clsx(
                    'relative flex items-center',
                )}
            >
                {labelComponent}

                <button
                    className={clsx(
                        'w-10 h-full cursor-pointer rounded-e-md border-s',
                        INPUT_VARIANTS[triggerOptions?.variant || 'none']
                    )}
                    onClick={handleClickArrow}
                >
                    <IoIosArrowDown
                        size={18}
                        className={clsx(
                            'mx-auto w-5 h-5 transition-transform duration-300',
                            isDropdownOpen ? 'rotate-180' : 'rotate-0',
                        )}
                    />
                </button>
            </div>

            <div
                ref={menuRef}
                className={clsx(
                    `absolute z-40 min-w-fit w-full transition-all duration-300`,
                    isDropdownOpen ? 'top-full opacity-100 visible' : 'top-[110%] invisible opacity-0',
                    menuOptions?.position === 'right' ? 'right-0' : 'left-0',
                )}
            >
                {buttons.map((button, index) => (
                    <div
                        key={index}
                        onClick={() => handleClickButton(button.shouldCloseOnClick)}
                    >
                        {button.component}
                    </div>
                ))}
            </div>
        </div>
    )
};

interface CopyToClipboardButtonProps {
    text: string;
    disabled?: boolean;
}

export const CopyToClipboardButton: React.FC<CopyToClipboardButtonProps> = (props) => {
    const { text, disabled = false } = props

    const [_, copy] = useCopyToClipboard()

    const [tooltip, setTooltip] = useState<string>('Copy to clipboard')

    return (
        <Button
            inputVariant='deep_ocean_text_transparent_bg'
            onClick={() => {
                if (disabled) return

                copy(text)
                    .then(() => {
                        setTooltip('Link copied!')

                        setTimeout(() => {
                            setTooltip('Copy to clipboard')
                        }, 500);
                    })
            }}
            shape='square'
            className='h-fit! hover:shadow-md'
            tooltip={tooltip}
            disabled={disabled}
        >
            <RiLinkM
                size={12}
            />
        </Button>
    )
}